<template>
  <div class="template-setup-container mt-24">
    <div class="wrapper grid">
      <div class="grid left">
        <div class="accordion">
          <div class="template-details accordion-item">
            <h2 id="templateHeadingDetails" class="accordion-header">
              <button
                aria-controls="collapseDetails"
                aria-expanded="true"
                class="accordion-button"
                data-bs-target="#collapseDetails"
                data-bs-toggle="collapse"
                type="button"
              >
                <div class="mb-0 d-flex align-items-center">
                  <div class="template-setup-accordion-icon me-8 d-flex align-items-center justify-content-center">
                    <v-icon icon="pencil" size="xs" />
                  </div>

                  <div>
                    <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("template.details") }}</div>
                    <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("template.detailsSubtitle") }}</div>
                  </div>
                </div>
              </button>
            </h2>

            <div
              id="collapseDetails"
              aria-labelledby="templateHeadingDetails"
              class="accordion-collapse collapse show"
              data-bs-parent="#accordionExample"
            >
              <div class="accordion-body pb-16 pb-xl-24">
                <p class="text-xs fw-medium text-muted mb-12" v-if="template.isCollaborator">
                  {{ $t("template.templateBelongsToCollaborator") }}
                </p>
                <form id="template-update-form" class="d-flex flex-column justify-content-between h-100">
                  <fieldset>
                    <legend class="visually-hidden">{{ $t("template.basicInformation") }}</legend>
                    <v-form-input
                      id="template-update-name"
                      v-model="name"
                      :label="`${$t('general.shared.name')}*`"
                      :yup-errors-variable="nameError"
                      autocomplete="off"
                      form-type="outline"
                      class="mb-8"
                    />

                    <v-form
                      id="template-update-travel-type-select"
                      :label="`${$t('template.travelType')}*`"
                      :yup-errors-variable="travelTypeError"
                      form-type="outline"
                      class="mb-8"
                    >
                      <v-select
                        id="template-update-travel-type-select"
                        v-model="travelType"
                        :clearable="false"
                        :options="travelTypes"
                        :reduce="(returnValue) => returnValue.key"
                      >
                        <template v-slot:no-options>
                          <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                        </template>
                      </v-select>
                    </v-form>

                    <v-form
                      id="template-update-modality-select"
                      :label="`${$t('template.modality')}*`"
                      :yup-errors-variable="modalityError"
                      form-type="outline"
                      class="mb-8"
                    >
                      <v-select
                        id="template-update-modality-select"
                        v-model="modality"
                        :clearable="false"
                        :options="modalityOptions"
                        :reduce="(returnValue) => returnValue.key"
                      >
                        <template v-slot:no-options>
                          <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                        </template>
                      </v-select>
                      <p class="text-xs text-muted mt-4 mb-0">{{ $t("template.modalityHelp") }}</p>
                    </v-form>

                    <v-input-address
                      id="template-update-origin-search"
                      ref="template-update-origin-search"
                      :get-address="true"
                      :get-radius-bounds="true"
                      :label="`${$t('general.shared.origin')}*`"
                      :radius-bounds="20000"
                      :value="searchLocation(origin)"
                      class="col-12 mb-8"
                      @address="createLocation($event, true)"
                      @input="checkLocationInput($event, true)"
                    />

                    <v-input-address
                      id="template-update-destination-search"
                      ref="template-update-destination-search-ref"
                      :get-address="true"
                      :get-radius-bounds="true"
                      :label="`${$t('general.shared.destination')}*`"
                      :radius-bounds="20000"
                      :value="searchLocation(destination)"
                      class="col-12 mb-8"
                      @address="createLocation($event, false)"
                      @input="checkLocationInput($event, false)"
                    />

                  </fieldset>
                  <v-button
                    :disabled="!meta.valid || disableButton || !meta.dirty"
                    :is-loading="accepted"
                    class="btn-icon w-fit align-self-end mt-12"
                    size="xs"
                    variant="primary"
                    @click="updateTemplate"
                  >
                    {{ $t("general.button.save") }}
                    <v-icon icon="arrow-right" size="xs" space="ms-8" />
                  </v-button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <the-template-setup-tour-terms-v2
          v-if="authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />

        <the-template-before-tour-v2
          v-if="authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />
        <the-template-luggage-v2
          v-if="authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />
        <the-activity-extras-v2
          v-if="authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />
        <div class="accordion">
          <div class="template-details accordion-item">
            <h2 id="headingArchive" class="accordion-header">
              <button
                aria-controls="collapseArchive"
                aria-expanded="false"
                class="accordion-button collapsed"
                data-bs-target="#collapseArchive"
                data-bs-toggle="collapse"
                type="button"
              >
                <div class="mb-0 d-flex align-items-center">
                  <div class="template-setup-accordion-icon me-8 d-flex align-items-center justify-content-center">
                    <v-icon icon="import" size="xs" />
                  </div>

                  <div>
                    <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("template.archiveTitle") }}</div>
                    <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("template.archiveSubtitle") }}</div>
                  </div>
                </div>
              </button>
            </h2>

            <div
              id="collapseArchive"
              aria-labelledby="headingArchive"
              class="accordion-collapse collapse"
              data-bs-parent="#accordionExample"
            >
              <div class="accordion-body d-flex flex-column pb-16 pb-xl-24">
                <div class="alert alert-warning d-flex align-items-center mb-12" role="alert">
                  <v-icon icon="info" size="md" space="me-12" class="text-dark flex-shrink-0" />

                  <div class="text-xs">
                    {{ $t("template.archiveText") }}
                  </div>
                </div>

                <v-button
                  v-if="!template.deletedAt"
                  id="btn-archive-template"
                  :disabled="disableArchiveBtn"
                  class="btn-icon mb-4 align-self-end mt-12"
                  size="xs"
                  variant="danger"
                  @click="archiveTemplate"
                >
                  <v-icon icon="import" space="me-8" />
                  <template v-if="disableArchiveBtn">{{ $t("general.button.archiving") }}</template>
                  <template v-else>{{ $t("general.button.archive") }}</template>
                </v-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid right">
        <the-template-setup-agencies-v2
          v-if="can('template.all') && authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />

        <the-template-setup-contacts-v2
          v-if="authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />

        <the-template-after-tour-v2
          v-if="authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />

        <the-activity-included-v2
          v-if="authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />
        <the-activity-excluded-v2
          v-if="authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />
        <the-template-setup-featured-image-v2
          v-if="authStore.agency"
          :agency="authStore.agency.slug"
          :template="template"
          @reload-template="emit('reloadTemplate')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { defineProps, defineEmits, ref, computed, onMounted, reactive, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { useField } from "vee-validate";
import vSelect from "vue-select";
import { VForm, VFormInput } from "@uniqoders/form";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import useFormValidation from "@/helpers/form";
import { useAuthStore } from "@/stores/auth";
import { mapConcepts } from "@/helpers/ConceptHelper";
import useConcepts from "@/helpers/Concepts";
import Template from "@/api/objects/Template";
import api from "@/api";
import VInputAddress from "@/components/vendor/basic/form/VInputAddress.vue";
import Location from "@/api/objects/Location";
import TheTemplateSetupAgenciesV2 from "@/components/template/V2/TheTemplateSetupAgenciesV2.vue";
import TheTemplateSetupContactsV2 from "@/components/template/parts/V2/TheTemplateSetupContactsV2.vue";
import useCapabilities from "@/helpers/capabilities";
import TheTemplateSetupTourTermsV2 from "@/components/template/V2/TheTemplateSetupTourTermsV2.vue";
import TheTemplateSetupFeaturedImageV2 from "@/components/template/V2/TheTemplateSetupFeaturedImageV2.vue";
import TheTemplateBeforeTourV2 from "@/components/template/V2/TheTemplateBeforeTourV2.vue";
import TheTemplateLuggageV2 from "@/components/template/V2/TheTemplateLuggageV2.vue";
import TheTemplateAfterTourV2 from "@/components/template/V2/TheTemplateAfterTourV2.vue";
import { useToast, POSITION } from "vue-toastification";
import { useRouter } from "vue-router";
import TheActivityIncludedV2 from "@/components/template/V2/TheActivityIncludedV2.vue";
import TheActivityExcludedV2 from "@/components/template/V2/TheActivityExcludedV2.vue";
import TheActivityExtrasV2 from "@/components/template/V2/TheActivityExtrasV2.vue";
import TheTemplateSetupMetaV2 from "@/components/template/V2/TheTemplateSetupMetaV2.vue";
import { buildModalitySelectOptions } from "@/helpers/ModalityHelper";

const props = defineProps<{
  activitiesId: string;
  template: Template;
}>();

const emit = defineEmits(["reloadTemplate"]);

const { t } = useI18n();
const toast = useToast();
const router = useRouter();
const { can } = useCapabilities();
const instance = getCurrentInstance();

const $modal = instance?.appContext.config.globalProperties.$modal;

const rules = yup.object({
  name: yup.string().required().label(t("general.shared.name")),
  travelType: yup.string().required().label(t("template.travelType")),
  modality: yup.string().required().oneOf(["self-guided", "group"]).label(t("template.modality")),
  origin: yup.mixed().required().nullable().label(t("general.shared.origin")),
  originSelected: yup.bool().oneOf([true], t("general.shared.selectOriginMessage")),
  destination: yup.mixed().required().nullable().label(t("general.shared.destination")),
  destinationSelected: yup.bool().oneOf([true], t("general.shared.selectDestinationMessage")),
});

const initialValues = reactive({
  name: props.template.name || "",
  travelType: props.template.travelType || "",
  modality: props.template.modality || "self-guided",
  origin: props.template.originId || null,
  destination: props.template.destinationId || null,
  originSelected: !!props.template.originId,
  destinationSelected: !!props.template.destinationId,
});

const form = useFormValidation(rules, initialValues);
const { accepted, meta, toggleAccepted, errors } = form;
const { value: name, errorMessage: nameError } = useField<string>("name");
const { value: travelType, errorMessage: travelTypeError } = useField<string | number | undefined>("travelType");
const { value: modality, errorMessage: modalityError } = useField<string>("modality");
const { value: origin } = useField<number | null>("origin");
const { value: destination } = useField<number | null>("destination");
const { value: originSelected } = useField<boolean>("originSelected");
const { value: destinationSelected } = useField<boolean>("destinationSelected");

const authStore = useAuthStore();

const concepts = useConcepts();
const travelTypes = computed(() => {
  return concepts.concepts.value ? mapConcepts(concepts.concepts.value["tour.travel_type"]) : [];
});

const modalityOptions = computed(() => buildModalitySelectOptions(t));

const locations = ref<Location[]>([]);
const highlightedDates = ref<Date[]>([]);
const disableButton = ref<boolean>(false);
const disableArchiveBtn = ref<boolean>(false);

const checkLocationInput = (value: string, isOrigin = false) => {
  if (isOrigin) {
    originSelected.value = false;
  } else {
    destinationSelected.value = false;
  }
};

const loadLocations = async () => {
  locations.value = await api.location.all();
};

const searchLocation = (locationId: number | null): string => {
  if (locationId === null) {
    return "";
  }
  const location = locations.value.find((loc) => loc.id === locationId);
  return location ? location.name : "";
};

const createLocation = async (address: Record<string, any>, isOrigin = true) => {
  try {
    const location = await api.location.create({
      name: address.name,
      provider_id: address.placeId,
      lat: address.lat,
      lng: address.lng,
      country: address.country.longName,
      province: address.regionProvince.longName,
      state: address.stateCommunity.longName,
    });

    await loadLocations();

    if (isOrigin) {
      origin.value = Number(location.id);
      originSelected.value = true;
    } else {
      destination.value = Number(location.id);
      destinationSelected.value = true;
    }
  } catch (error) {
    console.error("Error creating location:", error);
  }
};

const updateTemplate = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data = {
      name: name.value,
      travel_type: travelType.value,
      modality: modality.value,
      origin: origin.value,
      destination: destination.value,
      is_published: true,
      contact_text: props.template.contactText,
      terms_conditions_text: props.template.termsConditionsText,
    };

    if (authStore.agency) {
      await api.activityService.updateActivity(authStore.agency.slug, props.activitiesId, data);
      toast.success(t("general.shared.savedChanges"), {
        position: POSITION.BOTTOM_RIGHT,
      });
    }

    emit("reloadTemplate");

    disableButton.value = false;
    toggleAccepted();
  } catch (e: any) {
    disableButton.value = false;
    toggleAccepted();

    console.error(e);
    toast.error(e.response?.data?.message || "An error occurred.", {
      position: POSITION.BOTTOM_RIGHT,
    });
  }
};

const doArchiveTemplate = async () => {
  try {
    if (authStore.agency) {
      disableArchiveBtn.value = true;

      await api.activityService.archiveActivity(authStore.agency.slug, props.activitiesId);

      toast.success(t("general.shared.savedChanges"), {
        position: POSITION.BOTTOM_RIGHT,
      });

      await router.replace({ name: "activities.index" });

      disableArchiveBtn.value = false;
    }
  } catch (e: any) {
    disableArchiveBtn.value = false;
    console.error(e.response?.data);
    toast.error(e.response?.data?.message || "An error occurred.", {
      position: POSITION.BOTTOM_RIGHT,
    });
  }
};

const archiveTemplate = async () => {
  if (!instance) {
    return;
  }
  await instance.appContext.config.globalProperties.$modal.delete({
    title: t("template.archiveTitle"),
    text: t("template.archiveAlertText", { product: props.template.name }),
    deleteButtonText: t("general.button.archive"),
    deleteButtonLoadingText: t("general.button.archiving"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: doArchiveTemplate,
  });
};

onMounted(async () => {
  await loadLocations();
});
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
  grid-gap: 0.5rem 1rem;
}

.wrapper {
  grid-template-columns: 1fr;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
}

.grid.left {
  grid-column: 1 / span 1;
  grid-auto-rows: min-content;
}

.grid.right {
  grid-column: span 1 / -1;
  grid-auto-rows: min-content;
}

.grid.left > *:not(:last-child) {
  margin-bottom: 1rem;
}

.grid.right > *:not(:last-child) {
  margin-bottom: 1rem;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.9375rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.8125rem !important;
}

:deep(.accordion-button) {
  padding: 0.5rem 1rem !important;
}

:deep(.v-form-input),
:deep(.v-form) {
  margin-bottom: 0.25rem !important;
}
</style>
